import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import SectionCard from '../components/workflow/SectionCard';
import EngagementStageBadge from '../components/workflow/EngagementStageBadge';
import ClientEngagementTimeline, { RiskChip, riskRevisedText } from '../components/clients/ClientEngagementTimeline';
import ClientEntityHistory from '../components/shared/ClientEntityHistory';
import RefLink from '../components/shared/RefLink';
import NowName from '../components/shared/NowName';
import FileList from '../components/shared/FileList';
import { fmtBytes } from '../components/workflow/constants';
import { formatDate, formatDateShort, formatDateTime } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';
import { EntityCategoryChip, RelationshipBadge } from '../components/shared/entityCategory';

/**
 * One client, read-only (docs/modules/client-hub.md §3).
 *
 * Top to bottom: who they are (header + master record) → the headline
 * numbers (client since, engagements, ongoing, current stage, risk, EQR) →
 * the year-by-year engagement timeline (the core) → the group (sister
 * companies, structure chart versions) → appointment letters → the client
 * master's own history. Everything is served by ONE call so the page never
 * half-renders.
 */

const Chip = ({ children }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{children}</span>
);

const Tile = ({ label, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
    <p className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
    <div className="text-base font-semibold text-slate-900 mt-1 break-words">{children}</div>
  </div>
);

const fyeYear = (d) => String(d || '').slice(0, 4);

// Under the Risk Rating and EQR tiles. Both are set on a QC6/QC7 form, which
// may cover several engagements — so only its approval date, plus the
// addendum that revised the value, if any.
const QcSourceNote = ({ src }) => {
  if (!src) return null;
  const revised = riskRevisedText(src);
  return (
    <>
      {src.ratedOn && (
        <span className="block text-xs font-normal text-slate-500 mt-1">
          {src.qcType || 'QC6/QC7'} approved {formatDate(src.ratedOn)}
        </span>
      )}
      {revised && <span className="block text-xs font-normal text-amber-700">{revised}</span>}
    </>
  );
};

const ClientHubEntity = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const d = await fetchJSON(`/api/client-hub/entities/${id}`);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.status === 404 ? 'This client is not on the master list (it may have been removed).' : e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const entity = data && data.entity;
  const summary = data && data.summary;
  const engagements = (data && data.engagements) || [];
  const siblings = (data && data.siblings) || [];
  const structures = (data && data.structures) || [];
  const letters = (data && data.letters) || [];

  return (
    <RequestDetailShell backTo="/clients" backLabel="Client Hub" loading={!data} error={error}>
      {data && (
        <>
          <RequestHeader
            requestNo={entity.entityName}
            badge={<RelationshipBadge entity={entity} />}
            chips={(
              <>
                <Chip>UEN {entity.uen}</Chip>
                <EntityCategoryChip value={entity.entityCategory} />
                {entity.dateOfAppointment && <Chip>Appointed {formatDate(entity.dateOfAppointment)}</Chip>}
                {entity.clientGroupName && (
                  <Chip>
                    {entity.clientGroupName}
                    {entity.groupActive === false && <span className="ml-1 text-[10px] uppercase text-slate-400">inactive</span>}
                  </Chip>
                )}
              </>
            )}
          />

          {/* Headline numbers — all derived from the engagement rows below. */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            <Tile label="Client Since">
              {summary.firstFye ? `FYE ${fyeYear(summary.firstFye)}` : '—'}
            </Tile>
            <Tile label="Engagements">{summary.engagementCount}</Tile>
            <Tile label="Ongoing">{summary.ongoingCount}</Tile>
            <Tile label="Current Stage">
              {/* One row per stage the ongoing engagements sit at, with a count;
                  which engagement is where shows on the timeline below. */}
              {summary.stageBreakdown.length > 0 ? (
                <ul className="space-y-1">
                  {summary.stageBreakdown.map((s) => (
                    <li key={s.label} className="flex items-center justify-between gap-2">
                      <EngagementStageBadge stage={s} />
                      {s.count != null && <span className="text-sm">{s.count}</span>}
                    </li>
                  ))}
                </ul>
              ) : '—'}
            </Tile>
            <Tile label="Risk Rating">
              <RiskChip rating={summary.currentRisk} />
              <QcSourceNote src={summary.currentRiskSource} />
            </Tile>
            <Tile label="EQR">
              <NowName value={summary.currentEqr} now={summary.currentEqrNow} />
              <QcSourceNote src={summary.currentEqrSource} />
            </Tile>
          </div>

          <SectionCard title="Engagements, Year by Year" count={engagements.length > 0 ? `${engagements.length}` : null}>
            {summary.partner && (
              <p className="text-sm text-slate-600 mb-3">
                Partner in Charge (Latest): <span className="font-medium text-slate-900"><NowName value={summary.partner} now={summary.partnerNow} /></span>
                {summary.latestFye && <span className="text-slate-500"> · latest FYE {formatDateShort(summary.latestFye)}</span>}
              </p>
            )}
            <ClientEngagementTimeline engagements={engagements} />
          </SectionCard>

          <SectionCard title="Client Group">
            {!entity.clientGroupId ? (
              <p className="text-sm text-slate-500">Not part of a client group.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Other Companies in {entity.clientGroupName}
                  </p>
                  {siblings.length === 0 ? (
                    <p className="text-sm text-slate-500">This is the only company in the group.</p>
                  ) : (
                    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                      {siblings.map((s) => (
                        <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
                          <RefLink to={`/clients/${s.id}`} className="font-medium text-blue-700 hover:text-blue-900">{s.entityName}</RefLink>
                          <span className="font-mono text-xs text-slate-500">{s.uen}</span>
                          {!s.active && <RelationshipBadge entity={s} />}
                          <span className="ml-auto text-xs text-slate-500">
                            {s.engagementCount} engagement{s.engagementCount === 1 ? '' : 's'}
                            {s.latest ? ` · latest FYE ${formatDateShort(s.latest.yearEnd)} · ${s.latest.status}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <p className="text-[10.5px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Group Structure Chart
                  </p>
                  {structures.length === 0 ? (
                    <p className="text-sm text-slate-500">No structure chart on file for this group.</p>
                  ) : (
                    <FileList
                      files={structures}
                      row={(s, i) => ({
                        name: s.originalName,
                        // The hub lists files but never serves them — no
                        // download anywhere on this page.
                        meta: [i === 0 ? 'Current' : 'Previous', formatDateTime(s.createdAt), fmtBytes(s.sizeBytes)],
                        muted: i > 0,
                      })}
                    />
                  )}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Appointment Letters" count={letters.length > 0 ? `${letters.length}` : null}>
            {letters.length === 0 ? (
              <p className="text-sm text-slate-500">No appointment letter on file for this company.</p>
            ) : (
              <FileList
                files={letters}
                row={(l) => ({
                  name: l.originalName,
                  meta: [
                    l.supersededAt ? 'Superseded' : 'Current',
                    formatDateTime(l.createdAt),
                    fmtBytes(l.sizeBytes),
                    l.sourceRequestNo || null,
                  ],
                  muted: Boolean(l.supersededAt),
                })}
              />
            )}
          </SectionCard>

          <SectionCard title="Client Master History" collapsible defaultOpen={false}>
            <ClientEntityHistory fetchPath={`/api/client-hub/entities/${entity.id}/events`} />
          </SectionCard>
        </>
      )}
    </RequestDetailShell>
  );
};

export default ClientHubEntity;
